import React from "react";
import {
  Alert,
  Badge,
  Form,
  Icon,
  Input,
  InputNumber,
  Button,
  Select,
  Row,
  Col,
  Steps,
  Transfer, Radio,
} from "antd";
import get from "lodash/get";
import map from "lodash/map";
import { connect } from "react-redux";

import {
  updateSimulationRequest,
  createSimulationRequest,
  getSettingsUsersRequest,
  getSitesRequest,
  getSimulationSchemaRequest,
} from "../../api/requests";
import {
  getSimulationCreatePermission,
  getSimulationUpdatePermission,
} from "../../reducers/user/selectors";

import classes from "./SimulationForm.css";
import { logout } from "../../reducers/user/actions";
import { withNamespaces } from "react-i18next";

const { Option } = Select;
const { Step } = Steps;

const occlusionFails = (form) =>
  form.getFieldValue("ica_perc") &&
  form.getFieldValue("m2_mca_perc") &&
  form.getFieldValue("m1_mca_perc") &&
  form.getFieldValue("ica_perc") +
    form.getFieldValue("m2_mca_perc") +
    form.getFieldValue("m1_mca_perc") >
    100;

const aspectFails = (form) =>
  form.getFieldValue("aspects_8_10_perc") &&
  form.getFieldValue("aspects_5_7_perc") &&
  form.getFieldValue("aspects_8_10_perc") +
    form.getFieldValue("aspects_5_7_perc") >
    100;

const genderFails = (form) =>
  form.getFieldValue("sex_male_perc") &&
  form.getFieldValue("sex_female_perc") &&
  form.getFieldValue("sex_male_perc") + form.getFieldValue("sex_female_perc") >
    100;

class SimulationForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      usersList: [],
      userTargetKeys: props.user_ids || [],
      siteList: [],
      siteTargetKeys: props.site_ids || [],
      requestResult: {},
      currentStep: 0,
      currentUserPage: 1,
      total: 1,
      pageUserSize: 1000,
      name: "",
      simulationSchema: null,
    };
  }

  componentDidMount() {
    this.getUsersList();
    this.getSitesList();
    this.getSchema();
  }

  getSchema = async () => {
    try {
      const { data } = await getSimulationSchemaRequest();
      const simulations = {};
      data.forEach((item) => {
        simulations[item.group] = simulations[item.group]
          ? [...simulations[item.group], item]
          : [item];
      });

      this.setState({
        simulationSchema: simulations,
      });
    } catch (e) {
      console.log(e);
    }
  };

  getUsersList = async (page = this.state.currentUserPage) => {
    try {
      const { data } = await getSettingsUsersRequest({
        page,
        per_page: this.state.pageUserSize,
      });
      this.setState({
        usersList: data.data.map((user) => ({
          key: user.id,
          title: user.username,
          description: user.username,
          chosen: false,
        })),
        total: data.total_pages * this.state.pageUserSize,
        currentUserPage: page,
      });
    } catch (e) {
      console.log(e);
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
    }
  };

  getSitesList = async () => {
    try {
      const { data } = await getSitesRequest();
      this.setState({
        siteList: data.map((site) => ({ key: site.id, title: site.name })),
      });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      console.log(e);
    }
  };

  handleSubmit = (e) => {
    e.preventDefault();

    if (
      occlusionFails(this.props.form) ||
      aspectFails(this.props.form) ||
      genderFails(this.props.form)
    ) {
      return;
    }

    this.props.form.validateFields(async (err, values) => {
      this.setState({ isSubmitted: true, requestResult: {} });

      if (!err) {
        this.setState({ isLoading: true });

        try {
          const simulation = { ...values };

          simulation.name = this.state.name;
          simulation.user_ids = this.state.userTargetKeys;
          simulation.site_ids = this.state.siteTargetKeys;

          if (this.props.id) {
            await updateSimulationRequest(this.props.id, simulation);
          } else {
            await createSimulationRequest(simulation);
          }

          this.setState({
            isLoading: false,
            requestResult: {
              type: "success",
              message: this.props.i18n.t("Success"),
            },
          });

          setTimeout(() => {
            this.props.onSubmit(this.props.id);
          }, 1000);
        } catch (e) {
          if (get(e, "response.status") === 401) {
            this.props.logout();
            return;
          }
          this.setState({
            isLoading: false,
            requestResult: {
              type: "error",
              message: get(e, "response.data.message", this.props.i18n.t("Error")),
            },
          });

          if (get(e, "response.data.errors")) {
            const errors = {};
            Object.keys(get(e, "response.data.errors")).forEach(
              (errorFieldKey) => {
                errors[errorFieldKey] = {
                  value: values[errorFieldKey],
                  errors: get(e, `response.data.errors.${errorFieldKey}`).map(
                    (errorMessage) => new Error(errorMessage)
                  ),
                };
              }
            );
            this.props.form.setFields(errors);
          }
        }
      }
    });
  };

  onPrev = () => {
    this.setState((state) => ({ currentStep: state.currentStep - 1 }));
  };

  onNext = async () => {
    const { validateFields, getFieldValue } = this.props.form;

    const name = getFieldValue("name") || this.state.name;

    try {
      await validateFields();
      this.setState((state) => ({ currentStep: state.currentStep + 1, name }));
    } catch (e) {}
  };

  handleTransferChange = (targetName) => (targetKeys) => {
    this.setState({ [targetName]: targetKeys });
  };

  render() {
    const {
      isLoading,
      requestResult,
      currentStep,
      usersList,
      userTargetKeys,
      siteList,
      siteTargetKeys,
      simulationSchema,
    } = this.state;
    const { getFieldDecorator } = this.props.form;
    const {
      simulationUpdatePermission,
      simulationCreatePermission,
      id,
      isNew,
    } = this.props;

    // const isEditable = true;
    const isEditable = id
      ? simulationUpdatePermission
      : simulationCreatePermission;

    return (
      <div className={classes.wrapper}>
        <Form onSubmit={this.handleSubmit}>
          <h4 className="headline-default">
            {this.props.id ? (
              <span>
                {this.props.name}{" "}
                <Badge
                  count={this.props.i18n.t("Edit")}
                  style={{ backgroundColor: "#2968AE" }}
                />
              </span>
            ) : isNew ? (
              this.props.i18n.t("NamespaceSimulationForm:Add simulation")
            ) : (
              ""
            )}
          </h4>

          <br />

          <Steps current={currentStep}>
            <Step
              title={this.props.i18n.t("Step 1")}
              description={this.props.i18n.t(
                "NamespaceSimulationForm:Edit name"
              )}
            />
            <Step
              title={this.props.i18n.t("Step 2")}
              description={this.props.i18n.t(
                "NamespaceSimulationForm:Sites and users"
              )}
            />
            <Step
              title={this.props.i18n.t("Step 3")}
              description={this.props.i18n.t(
                "NamespaceSimulationForm:Edit simulation"
              )}
            />
          </Steps>

          <br />

          {currentStep === 0 && (
            <div>
              <Row gutter={12}>
                <Col span={12}>
                  <label>
                    <b>{this.props.i18n.t("Name")}:</b>
                    <Form.Item>
                      {getFieldDecorator("name", {
                        rules: [{ required: true, message: this.props.i18n.t("Required") }],
                        initialValue:
                          get(this.state, "name") ||
                          get(this.props, "name", ""),
                      })(
                        <Input
                          disabled={!isEditable}
                          placeholder={this.props.i18n.t("Name")}
                        />
                      )}
                    </Form.Item>
                  </label>
                </Col>
              </Row>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <Row gutter={12}>
                <Col>
                  <div>
                    <b>{this.props.i18n.t("Select users")}:</b>
                    <Form.Item>
                      {getFieldDecorator("user_ids", {
                        rules: [{ required: true, message: this.props.i18n.t("Required") }],
                        initialValue:
                          this.state.userTargetKeys &&
                          this.state.userTargetKeys.length > 0
                            ? this.state.userTargetKeys
                            : get(this.props, "user_ids", ""),
                      })(
                        <Transfer
                          dataSource={usersList}
                          targetKeys={userTargetKeys}
                          onChange={this.handleTransferChange("userTargetKeys")}
                          showSearch
                          render={(item) => item.title}
                          listStyle={{
                            height: 250,
                          }}
                        />
                      )}
                    </Form.Item>
                  </div>
                </Col>
                <Col>
                  <div>
                    <b>{this.props.i18n.t("Select sites")}:</b>
                    <Form.Item>
                      {getFieldDecorator("site_ids", {
                        rules: [{ required: true, message: this.props.i18n.t("Required") }],
                        initialValue:
                          this.state.siteTargetKeys &&
                          this.state.siteTargetKeys.length > 0
                            ? this.state.siteTargetKeys
                            : get(this.props, "site_ids", ""),
                      })(
                        <Transfer
                          dataSource={siteList}
                          targetKeys={siteTargetKeys}
                          onChange={this.handleTransferChange("siteTargetKeys")}
                          showSearch
                          render={(item) => item.title}
                          listStyle={{
                            height: 250,
                          }}
                        />
                      )}
                    </Form.Item>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <Row gutter={12}>
                <Col lg={12}>
                  <label>
                    <b>
                      {this.props.i18n.t(
                        "NamespaceSimulationForm:Number of patients"
                      )}
                      :
                    </b>
                    <Form.Item>
                      {getFieldDecorator("subject_count", {
                        rules: [{ required: true, message: this.props.i18n.t("Required") }],
                        initialValue: get(this.props, "subject_count", ""),
                      })(
                        <Input
                          disabled={!isEditable}
                          placeholder={this.props.i18n.t(
                            "NamespaceSimulationForm:Subject count"
                          )}
                        />
                      )}
                    </Form.Item>
                  </label>
                </Col>

                <Col lg={12}>
                  <label>
                    <b>
                      {this.props.i18n.t(
                        "NamespaceSimulationForm:Number of Simulations"
                      )}
                      :
                    </b>
                    <Form.Item>
                      {getFieldDecorator("simulation_count", {
                        rules: [{ required: true, message: this.props.i18n.t("Required") }],
                        initialValue: get(this.props, "simulation_count", ""),
                      })(
                        <Input
                          disabled={!isEditable}
                          placeholder={this.props.i18n.t(
                            "NamespaceSimulationForm:Simulation count"
                          )}
                        />
                      )}
                    </Form.Item>
                  </label>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col lg={12}>
                  <label>
                    <b>
                      {this.props.i18n.t(
                        "NamespaceSimulationForm:Number of patients"
                      )}
                      :
                    </b>
                    <Form.Item label={this.props.i18n.t("Randomization algorithm")}>
                        {getFieldDecorator("algorithm_type", {
                          rules: [

                          ],
                          initialValue: get(this.props, "algorithm_type", ""),
                        })(

                          <Select
                            placeholder={this.props.i18n.t("Select")}
                          >
                            <Option value="">--{this.props.i18n.t("FROM CONFIG")}--</Option>
                            <Option value="true_random">{this.props.i18n.t("True Random")}</Option>
                            <Option value="biased_coin">{this.props.i18n.t("Biased Coin")}</Option>
                            <Option value="mstb_algorithm">
                              {this.props.i18n.t("MSTB algorithm (Escape)")}
                            </Option>
                            <Option value="permuted_blocks">
                              {this.props.i18n.t("Permuted Blocks")}
                            </Option>
                          </Select>


                        )}
                      </Form.Item>
                  </label>
                </Col>

              </Row>

              {simulationSchema &&
                map(Object.keys(simulationSchema), (simulationGroup) => (
                  <Row gutter={12} key={simulationGroup}>
                    {map(simulationSchema[simulationGroup], ({ fieldName, label, isRequired }) => (
                      <Col lg={6} key={fieldName}>
                        <label>
                          <b>{label}</b>
                          <Form.Item>
                            {getFieldDecorator(fieldName, {
                              rules: [{ required: isRequired, message: this.props.i18n.t("Required") }],
                              initialValue: get(this.props, fieldName, ""),
                            })(
                              <InputNumber
                                style={{ width: "100%" }}
                                disabled={!isEditable}
                                placeholder={label}
                              />
                            )}
                          </Form.Item>
                        </label>
                      </Col>
                    ))}
                  </Row>
                ))}

              <Row gutter={12}>
                <Col lg={12}>
                  <label>
                    <b>{this.props.i18n.t("Status")}:</b>
                    <Form.Item>
                      {getFieldDecorator("status", {
                        rules: [{ required: true, message: this.props.i18n.t("Required") }],
                        initialValue: get(this.props, "status", "not_started"),
                      })(
                        <Select placeholder={this.props.i18n.t("Select")}>
                          <Option value="not_started">
                            {this.props.i18n.t("NamespaceSimulationForm:Draft")}
                          </Option>
                          <Option value="ready">
                            {this.props.i18n.t("NamespaceSimulationForm:Ready")}
                          </Option>
                          <Option disabled value="in_progress">
                            {this.props.i18n.t("NamespaceSimulationForm:In progress")}
                          </Option>
                          <Option disabled value="completed">
                            {this.props.i18n.t("NamespaceSimulationForm:Completed")}
                          </Option>
                        </Select>
                      )}
                    </Form.Item>
                  </label>
                </Col>
              </Row>
            </div>
          )}

          {requestResult.type && (
            <Alert
              message={requestResult.message}
              type={requestResult.type}
              className={classes.submitResult}
            />
          )}

          <div className={classes.actionWrapper}>
            {isEditable && currentStep !== 0 && (
              <Form.Item>
                <Button
                  type="default"
                  htmlType="button"
                  className={classes.submitButton}
                  onClick={this.onPrev}
                >
                  {this.props.i18n.t("Prev")}
                  {isLoading && <Icon type="loading" />}
                </Button>
                &nbsp; &nbsp;
              </Form.Item>
            )}
            {isEditable && currentStep === 2 && (
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className={classes.submitButton}
                >
                  {this.props.i18n.t("Save")}
                  {isLoading && <Icon type="loading" />}
                </Button>
                &nbsp; &nbsp;
              </Form.Item>
            )}
            {isEditable && currentStep !== 2 && (
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="button"
                  className={classes.submitButton}
                  onClick={this.onNext}
                >
                  {this.props.i18n.t("Next")}
                </Button>
                &nbsp; &nbsp;
              </Form.Item>
            )}

            <Form.Item>
              <Button
                type="default"
                htmlType="button"
                className={classes.submitButton}
                onClick={this.props.onCancel}
              >
                {this.props.i18n.t("Cancel")}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  simulationCreatePermission: getSimulationCreatePermission(state),
  simulationUpdatePermission: getSimulationUpdatePermission(state),
});

const mapDispatchToProps = { logout };

export default Form.create({ name: "simulation" })(
  connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(SimulationForm))
);



// WEBPACK FOOTER //
// ./src/components/SimulationForm/SimulationForm.js